import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Linking, Pressable } from 'react-native'
import { OverlayProvider, Tooltip as RNTooltip } from 'react-native-popper'

import styled, { useTheme } from 'styled-components/native'

import {
  INTEGRATED_BENEFITS_PROGRAM_HELP_CENTER_URL,
  NUCLEAR_BADGE_HELP_CENTER_URL,
} from '@lyrahealth-inc/shared-app-logic/src/features/profile/constants'
import { specialAlertBadges, SpecialAlertType } from '@lyrahealth-inc/shared-app-logic/src/features/profile/messages'

import { ActivityBadge } from '../../atoms/activityBadge/ActivityBadge'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface ProfileCardBadgeProps {
  text: string
  specialAlertType?: string
}

export const RISK_BADGE_TEXT = 'Risk'
export const SELF_HARM_BADGE_TEXT = 'Self harm'
export const SPECIAL_ALERT_BADGE_TEXT = 'Special Alert'

const BADGE_CONFIGS = {
  [RISK_BADGE_TEXT]: {
    variant: BadgeVariant.ALERT,
  },
  [SELF_HARM_BADGE_TEXT]: {
    variant: BadgeVariant.ALERT,
  },
  [SPECIAL_ALERT_BADGE_TEXT]: {
    variant: BadgeVariant.ATTENTION,
  },
}

// Default to INFO_SECONDARY for program names
const defaultProgramStyle = {
  variant: BadgeVariant.INFO_SECONDARY,
}

const SpacerView = styled.View({
  height: '8px',
})

const SmallSpacerView = styled.View({
  width: '4px',
})

const TooltipContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  backgroundColor: 'black',
  paddingHorizontal: spacing['12px'],
  paddingVertical: spacing['8px'],
  borderRadius: spacing['4px'],
  maxWidth: 250,
}))

// ---------- Main Component ----------

export const ProfileCardBadge: React.FC<ProfileCardBadgeProps> = ({ text, specialAlertType }) => {
  const theme = useTheme() as ThemeType
  const { Content, Arrow } = RNTooltip

  // Check if this badge should have a tooltip
  const hasTooltip = text === 'Special Alert'

  // Tooltip open/close state
  const [open, setOpen] = useState(false)

  const handleBadgePress = () => {
    setOpen(!open)
  }

  // Called by the Popper if the user presses ESC or clicks outside the tooltip
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
  }

  // Automatically detect badge type based on text
  const getBadgeConfig = (badgeText: string) => {
    if (BADGE_CONFIGS[badgeText as keyof typeof BADGE_CONFIGS]) {
      return BADGE_CONFIGS[badgeText as keyof typeof BADGE_CONFIGS]
    }
    return defaultProgramStyle
  }

  const { variant } = getBadgeConfig(text)

  const badgeContent = (
    <Pressable
      accessibilityRole='button'
      accessibilityLabel='Show tooltip information'
      testID={tID(`${text}-Badge`)}
      onPress={hasTooltip ? handleBadgePress : undefined}
      disabled={!hasTooltip}
    >
      <ActivityBadge text={text} variant={variant} shape={BadgeShape.RECT} preserveCase bold />
    </Pressable>
  )

  // Only render tooltip wrapper if badge has tooltip
  if (!hasTooltip) {
    return badgeContent
  }

  return (
    <OverlayProvider>
      <RNTooltip isOpen={open} onOpenChange={handleOpenChange} placement='top' on='press' trigger={badgeContent}>
        <Content>
          <TooltipContainer>
            <Title
              text={<FormattedMessage {...specialAlertBadges[specialAlertType as SpecialAlertType].title} />}
              size={TitleSize.XSMALL}
              color={theme.colors.textInverse}
            />
            <SpacerView />
            <BodyText
              text={<FormattedMessage {...specialAlertBadges[specialAlertType as SpecialAlertType].description} />}
              size={BodyTextSize.SMALL}
              color={theme.colors.textInverse}
            />
            <SpacerView />
            <Pressable
              onPress={() => {
                Linking.openURL(
                  specialAlertType === 'limitedAccess'
                    ? INTEGRATED_BENEFITS_PROGRAM_HELP_CENTER_URL
                    : NUCLEAR_BADGE_HELP_CENTER_URL,
                )
              }}
            >
              <BodyText
                text={<FormattedMessage {...specialAlertBadges[specialAlertType as SpecialAlertType].linkText} />}
                size={BodyTextSize.SMALL}
                color={theme.colors.textInverse}
                style={{ textDecorationLine: 'underline' }}
              />
              <SmallSpacerView />
            </Pressable>
          </TooltipContainer>
          <Arrow style={{ backgroundColor: 'black' }} height={8} width={8} />
        </Content>
      </RNTooltip>
    </OverlayProvider>
  )
}
